import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AuthenticationContext } from 'contexts/authentication.context';
import { getIdToken } from 'utils/utils';
import { getAssetsWithBuildingAndEstimate, getOutdoorEstimates } from '../utils/utils';
import AssetsApi from 'api/assets/assets.api';
import { MapControlContext } from './mapcontrol.context';
import { alertErrorMessage } from 'utils/alerts';
import { VenuesContext } from './venues.context';
import { FullConfigContext } from 'contexts/fullConfig.context';
import { EstimateType } from 'types/Estimate';
import { SearchFilterContext } from './searchFilter.context';
import useURLParamEffects from '../hooks/useURLParamEffects';

type AssetsContextTypes = {
  setRawIndoorAssets: (arg: EstimateType[]) => void;
  rawIndoorAssets: EstimateType[];
  rawOutsideMappedBuildingsAssets: EstimateType[];
  setRawOutsideMappedBuildingsAssets: (arg: EstimateType[]) => void;
};
export const AssetsContext = createContext({} as AssetsContextTypes);

type AssetsProviderProps = { children: ReactNode };

// A context that fetches all Registered Beacons filtered by selectedVenue, and passes them into context to display as assets on map.
const AssetsProvider = ({ children }: AssetsProviderProps) => {
  const [rawIndoorAssets, setRawIndoorAssets] = useState<EstimateType[] | []>([]); // raw unfiltered assets straight from API.
  const [rawOutsideMappedBuildingsAssets, setRawOutsideMappedBuildingsAssets] = useState<
    EstimateType[] | []
  >([]);
  const { recallGetAssets, setRecallGetAssets, setApiCallInProgress, setAssetsApiCallInProgress } =
    useContext(MapControlContext);
  const { setInSearchMode } = useContext(SearchFilterContext);

  const authContext = useContext(AuthenticationContext).authState;
  const { assetTrackingPermissionEnabled } = useContext(FullConfigContext);
  const { selectedVenueObj, isSingleVenue } = useContext(VenuesContext);
  const selectedVenueID = isSingleVenue ? null : selectedVenueObj.venue_id;
  const token = getIdToken(authContext);

  const value: AssetsContextTypes = {
    setRawIndoorAssets,
    setRawOutsideMappedBuildingsAssets,
    rawIndoorAssets,
    rawOutsideMappedBuildingsAssets,
  };
  const { checkHandleMatchedAssetExistsFromAPI } = useURLParamEffects();

  function callGetAssets() {
    if (assetTrackingPermissionEnabled) {
      const assetsApi = new AssetsApi(token);
      setApiCallInProgress(true);
      setAssetsApiCallInProgress(true);

      assetsApi
        .getRegisteredBeaconMapAssets(selectedVenueID)
        .then((res) => {
          const { items } = res.data;
          const filteredItems = getAssetsWithBuildingAndEstimate(items);
          const outdoorFilteredItems = getOutdoorEstimates(items) as EstimateType[];

          setRawIndoorAssets(filteredItems);
          setRawOutsideMappedBuildingsAssets(outdoorFilteredItems);
          setApiCallInProgress(false);
          setAssetsApiCallInProgress(false);
          setRecallGetAssets(false);
          setInSearchMode(false);

          checkHandleMatchedAssetExistsFromAPI([...filteredItems, ...outdoorFilteredItems]);
        })
        .catch((err) => {
          alertErrorMessage('Could not fetch Assets');
          setApiCallInProgress(false);
          setAssetsApiCallInProgress(false);
          console.error(err.message);
        });
    }
  }

  useEffect(() => {
    // recall API after search has been cleared.
    if (recallGetAssets) {
      callGetAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recallGetAssets]);

  return <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>;
};

export default AssetsProvider;
